import React, { useState } from "react";
import { Plus, Paperclip, BarChart3, Building2, User, Trash2, Target } from "lucide-react";

import {
    MainLayout,
    AppButton,
    SearchInput,
    ListToolbar,
    Pagination,
    LoadingState,
    EmptyState,
    ErrorBanner,
    ConfirmModal,
    MessageModal,
    MessageState,
    useDebouncedValue,
} from "../ui/components";
import CrmContactDetail from "./CrmContactDetail";
import { usePaginatedList } from "../../api/usePaginatedList";
import { createEntry, deleteEntry, toUserMessage, updateEntry } from "../../api/strapi";
import { isAdmin as isAdminRole } from "../../types/roles";
import { Contact, ContactFields, UserProfile } from "../../types/objects";

const KIND_ICON = {
    "Společnost": <Building2 size={24} />,
    "Osoba": <User size={24} />,
    "Lead": <Target size={24} />,
};

const scoreClass = (score: number) => (score >= 70 ? "text-green-600" : score >= 40 ? "text-orange-500" : "text-red-500");

/**
 * CRM contacts: searchable list ordered by overall score, create / edit form,
 * Admin-only delete (enforced by the hub policy as well).
 */
export default function CrmContacts({ user }: { user: UserProfile }) {
    const isAdmin = isAdminRole(user.role?.name);
    const [searchTerm, setSearchTerm] = useState("");
    const search = useDebouncedValue(searchTerm);
    const list = usePaginatedList<Contact>("contacts", { search, sort: "score_final:desc" });

    const [formOpen, setFormOpen] = useState(false);
    const [selected, setSelected] = useState<Contact | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<MessageState | null>(null);

    const openForm = (contact: Contact | null) => {
        setSelected(contact);
        setFormOpen(true);
    };

    /** Rejected promises are shown inside the form (CrmContactDetail). */
    const handleSave = async (payload: ContactFields, documentId: string | null) => {
        if (documentId) {
            await updateEntry("contacts", documentId, { ...payload });
        } else {
            await createEntry("contacts", { ...payload });
        }
        await list.reload();
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            await deleteEntry("contacts", deleteTarget.documentId);
            setDeleteTarget(null);
            await list.reload();
        } catch (err) {
            console.error("[contacts] Delete failed", err);
            setDeleteTarget(null);
            setMessage({ type: "error", title: "Chyba při mazání", text: toUserMessage(err, "Kontakt se nepodařilo smazat.") });
        } finally {
            setBusy(false);
        }
    };

    const body = () => {
        if (list.loading && list.rows.length === 0) return <LoadingState />;
        if (list.rows.length === 0) {
            return <EmptyState label={search ? "Hledání neodpovídá žádný kontakt." : "V CRM zatím nejsou žádné kontakty."} />;
        }
        return (
            <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
                <div className="hidden md:grid grid-cols-4 bg-slate-50/50 border-b border-slate-100 px-8 py-5 text-[10px] text-slate-400 tracking-widest font-black uppercase">
                    <div>Subjekt / identifikace</div>
                    <div className="text-center">Skóre</div>
                    <div className="text-center">Přílohy</div>
                    <div className="text-right pr-4">Akce</div>
                </div>
                <div className="divide-y divide-slate-50 text-left">
                    {list.rows.map((contact) => (
                        <div
                            key={contact.documentId}
                            className="p-6 md:px-8 md:py-6 flex flex-col md:grid md:grid-cols-4 gap-4 md:gap-0 hover:bg-blue-50/40 transition-all cursor-pointer group items-center"
                            onClick={() => openForm(contact)}
                        >
                            <div className="w-full flex items-center gap-4 min-w-0">
                                <div className="p-3 bg-slate-100 rounded-2xl text-slate-400 group-hover:text-blue-600 transition-all shrink-0" title={contact.kind}>
                                    {KIND_ICON[contact.kind] ?? KIND_ICON.Lead}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-black text-slate-900 tracking-tight leading-tight truncate">{contact.fullname}</div>
                                    <div className="text-xs font-semibold text-slate-400 mt-1 truncate">{contact.email}</div>
                                </div>
                            </div>
                            <div className="w-full flex justify-between md:justify-center items-center border-y border-slate-50 py-2 md:border-none">
                                <span className="md:hidden text-[10px] text-slate-400 font-black uppercase">Skóre</span>
                                <div className={`inline-flex items-center gap-2 font-black text-lg ${scoreClass(contact.score_final)}`}>
                                    <BarChart3 size={16} /> {contact.score_final}%
                                </div>
                            </div>
                            <div className="w-full flex justify-between md:justify-center items-center">
                                <span className="md:hidden text-[10px] text-slate-400 font-black uppercase">Přílohy</span>
                                <div className="flex items-center gap-1.5 text-slate-400 font-black">
                                    <Paperclip size={14} /> <span className="text-xs">{contact.attachments?.length || 0}</span>
                                </div>
                            </div>
                            <div className="w-full flex justify-end gap-3 font-black">
                                <button type="button" className="flex-1 md:flex-none px-6 py-3 rounded-2xl bg-slate-800 text-white text-[11px] uppercase font-black hover:bg-slate-700 transition-all">
                                    Detail
                                </button>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteTarget(contact);
                                        }}
                                        className="flex-1 md:flex-none px-6 py-3 rounded-2xl bg-red-500 text-white text-[11px] uppercase font-black flex items-center justify-center gap-2 hover:bg-red-600 shadow-lg shadow-red-200 transition-all active:scale-95"
                                    >
                                        <Trash2 size={16} /> Smazat
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <MainLayout
            title="Kontakty"
            subtitle="Subjekty v CRM seřazené podle celkového skóre."
            actions={
                <AppButton variant="success" onClick={() => openForm(null)} className="px-8 shadow-lg shadow-green-200">
                    <Plus size={20} /> Nový subjekt
                </AppButton>
            }
        >
            <ListToolbar total={list.pagination.total} pageSize={list.pageSize} loading={list.loading} onRefresh={list.reload} onPageSizeChange={list.setPageSize}>
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Hledat jméno, e-mail nebo telefon…" />
            </ListToolbar>

            <div className="space-y-6">
                {list.error && <ErrorBanner message={list.error} onRetry={list.reload} />}
                {body()}
                <Pagination page={list.page} pageCount={list.pagination.pageCount} total={list.pagination.total} onPageChange={list.setPage} />
            </div>

            <CrmContactDetail isOpen={formOpen} onClose={() => setFormOpen(false)} initialData={selected} onSave={handleSave} />

            <ConfirmModal
                open={!!deleteTarget}
                title="Smazat subjekt?"
                text={<>Opravdu chcete trvale odstranit záznam <strong className="text-red-600">„{deleteTarget?.fullname}“</strong>?</>}
                busy={busy}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
            <MessageModal message={message} onClose={() => setMessage(null)} />
        </MainLayout>
    );
}
